// HighlightsPage: Event highlights
import React, { useEffect, useState, useRef } from 'react';
import PublicLayout from '../layouts/PublicLayout';
import { Box, Typography, Card, CardContent, IconButton, Fade, Dialog, DialogTitle, DialogContent, DialogActions, Button, Slide, Chip, Grid } from '@mui/material';
import api from '../services/api';
import { getCMS } from '../services/cms';
import { useTheme } from '@mui/material/styles';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import CloseIcon from '@mui/icons-material/Close';

function HighlightsPage() {
  const [highlights, setHighlights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogIndex, setDialogIndex] = useState(null);
  const [readingMode, setReadingMode] = useState(false);
  const [bgMap, setBgMap] = useState({});
  const timerRef = useRef();
  const theme = useTheme();
  const decoImages = [
    'https://i.postimg.cc/PLTwMSJb/Copilot-20260117-135237.png',
    'https://i.postimg.cc/McqQY9H1/drum.png',
    'https://i.postimg.cc/WDTk75z5/maasai-Face.png',
    'https://i.postimg.cc/KRHvGpxg/elephant.png',
    'https://i.postimg.cc/svNDjHsS/gazelle.png',
    'https://i.postimg.cc/2VK8k9rZ/girafe.png',
    'https://i.postimg.cc/mt52LpBS/lion.png',
    'https://i.postimg.cc/3dcJ8fY0/zebra.png',
  ];

  useEffect(() => {
    setLoading(true);
    api.get('/api/cms')
      .then(res => {
        const items = (res.data || []).filter(i => i.type === 'highlight');
        const all = items.flatMap(i => Array.isArray(i.contentJSON?.items) ? i.contentJSON.items : []);
        setHighlights(all || []);
      })
      .catch(() => setError('Failed to load highlights'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    let mounted = true;
    getCMS()
      .then(items => {
        if (!mounted) return;
        const landing = (items || []).find(i => i.type === 'landing');
        setBgMap(landing?.contentJSON?.backgrounds || {});
      })
      .catch(() => {})
      .finally(() => { mounted = false; });
  }, []);

  useEffect(() => {
    if (highlights.length > 1) {
      timerRef.current = setTimeout(() => setDialogIndex(i => (i === null ? 0 : (i + 1) % highlights.length)), 5000);
      return () => clearTimeout(timerRef.current);
    }
  }, [highlights]);

  useEffect(() => {
    // clear auto-advance while dialog is open
    if (dialogIndex !== null && timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, [dialogIndex]);

  function openDetail(i) { setDialogIndex(i); }
  function closeDetail() { setDialogIndex(null); setReadingMode(false); }

  return (
    <PublicLayout>
      <Box sx={{ position: 'relative', width: '100%', minHeight: '56vh', py: { xs: 6, md: 8 }, overflow: 'hidden' }}>
        {bgMap.highlights && (
          <Box sx={{ position: 'absolute', inset: 0, backgroundImage: `url(${bgMap.highlights})`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', zIndex: 0 }} />
        )}
        {bgMap.highlights && (
          <Box sx={{ position: 'absolute', inset: 0, bgcolor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.22)' : 'rgba(255,255,255,0.12)', zIndex: 0 }} />
        )}

        <Box sx={{ maxWidth: '100vw', width: '100%', mx: 'auto', p: { xs: 1, sm: 2, md: 3 }, position: 'relative', zIndex: 1 }}>
          <Box sx={{ maxWidth: 1100, mx: 'auto', bgcolor: 'background.paper', borderRadius: 3, boxShadow: 4, p: { xs: 2, md: 3 } }}>
            {loading ? (
              <Typography>Loading...</Typography>
            ) : error ? (
              <Typography color="error">{error}</Typography>
            ) : highlights.length === 0 ? (
              <Typography color="text.secondary">No highlights available at this time.</Typography>
            ) : highlights.length === 1 ? (
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Card sx={{ width: { xs: '100%', sm: 600 }, boxShadow: 6 }}>
                  {highlights[0].image && <Box sx={{ height: 260, overflow: 'hidden' }}><img src={highlights[0].image} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /></Box>}
                  <CardContent>
                    <Typography variant="h5" fontWeight={800}>{highlights[0].heading}</Typography>
                    <Typography sx={{ color: 'text.secondary', whiteSpace: 'pre-line' }}>{highlights[0].story}</Typography>
                  </CardContent>
                </Card>
              </Box>
            ) : (
              <Box>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  {highlights.map((item, idx) => (
                    <Grid item xs={6} sm={4} md={3} key={idx}>
                      <Fade in timeout={400 + idx * 80}>
                        <Card
                          onClick={() => openDetail(idx)}
                          sx={{
                            position: 'relative',
                            cursor: 'pointer',
                            overflow: 'hidden',
                            borderRadius: 2,
                            boxShadow: 4,
                            transition: 'transform 0.18s, box-shadow 0.18s',
                            '&:hover': { transform: 'translateY(-6px)', boxShadow: 8 }
                          }}
                        >
                          {item.image && (
                            <Box sx={{ position: 'relative', height: { xs: 140, sm: 160 }, overflow: 'hidden' }}>
                              <Box component="img" src={item.image} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                              <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(0,0,0,0.0) 0%, rgba(0,0,0,0.36) 70%)' }} />
                            </Box>
                          )}
                          {/* decorative side image (subtle) */}
                          {/* halo behind decorative image for contrast */}
                          <Box sx={{
                            position: 'absolute',
                            top: '50%',
                            left: idx % 2 === 0 ? 4 : 'auto',
                            right: idx % 2 !== 0 ? 4 : 'auto',
                            transform: 'translateY(-50%)',
                            width: { xs: 84, sm: 108 },
                            height: { xs: 84, sm: 108 },
                            borderRadius: '50%',
                            bgcolor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.36)' : 'rgba(255,255,255,0.72)',
                            opacity: theme.palette.mode === 'dark' ? 0.06 : 0.06,
                            zIndex: 2,
                            pointerEvents: 'none'
                          }} />
                          <Box sx={{
                            position: 'absolute',
                            top: '50%',
                            left: idx % 2 === 0 ? 8 : 'auto',
                            right: idx % 2 !== 0 ? 8 : 'auto',
                            transform: 'translateY(-50%)',
                            width: { xs: 96, sm: 120 },
                            height: { xs: 96, sm: 120 },
                            opacity: 0.36,
                            zIndex: 3,
                            pointerEvents: 'none',
                            filter: 'drop-shadow(0 6px 18px rgba(0,0,0,0.28)) saturate(1.06) contrast(1.1)'
                          }}
                            style={{ backgroundImage: `url(${decoImages[idx % decoImages.length]})`, backgroundRepeat: 'no-repeat', backgroundPosition: 'center', backgroundSize: 'contain' }}
                          />
                          <CardContent>
                            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>{item.heading}</Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{item.story}</Typography>
                          </CardContent>
                        </Card>
                      </Fade>
                    </Grid>
                  ))}
                </Grid>

                <Dialog open={dialogIndex !== null} onClose={closeDetail} fullWidth maxWidth="lg" TransitionComponent={Slide} TransitionProps={{ direction: 'up' }}>
                  <DialogTitle sx={{ m: 0, p: 2, bgcolor: 'primary.dark', color: '#fff', position: 'relative' }}>
                    {highlights[dialogIndex]?.heading}
                    <IconButton aria-label="close" onClick={closeDetail} sx={{ position: 'absolute', right: 8, top: 8, color: '#fff' }}><CloseIcon /></IconButton>
                  </DialogTitle>
                  <DialogContent dividers sx={{ p: 0, position: 'relative' }}>
                      <IconButton onClick={() => setDialogIndex(i => (i - 1 + highlights.length) % highlights.length)} sx={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', zIndex: 5, bgcolor: 'rgba(0,0,0,0.35)', color: '#fff' }}><ArrowBackIosNewIcon /></IconButton>
                      <IconButton onClick={() => setDialogIndex(i => (i + 1) % highlights.length)} sx={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', zIndex: 5, bgcolor: 'rgba(0,0,0,0.35)', color: '#fff' }}><ArrowForwardIosIcon /></IconButton>
                      {/* dialog content area: show large image with cultural background overlay and a translucent story card */}
                      <Box sx={{ position: 'relative', width: '100%', height: { xs: 260, sm: 420 }, overflow: 'hidden', backgroundColor: 'transparent' }}>
                        {highlights[dialogIndex]?.image && (
                          <Box sx={{ width: '100%', height: '100%', overflow: 'hidden' }}>
                            <img src={highlights[dialogIndex]?.image} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', filter: 'brightness(0.86)' }} />
                          </Box>
                        )}
                        {/* subtle cultural background overlay (from page cms) */}
                        <Box sx={{ position: 'absolute', inset: 0, backgroundImage: `url(${bgMap.highlights || ''})`, backgroundSize: 'cover', backgroundPosition: 'center', opacity: 0.12, pointerEvents: 'none' }} />
                      </Box>
                      <Box sx={{ p: { xs: 2, sm: 4 }, display: 'flex', justifyContent: 'center' }}>
                        <Box sx={{ position: 'relative', width: '100%', maxWidth: 980, bgcolor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.46)' : 'rgba(255,255,255,0.86)', borderRadius: 2, boxShadow: 6, p: { xs: 2, sm: 4 } }}>
                          {dialogIndex !== null && (
                            <>
                              <Box sx={{
                                position: 'absolute',
                                top: '50%',
                                left: (dialogIndex % 2 === 0) ? 8 : 'auto',
                                right: (dialogIndex % 2 !== 0) ? 8 : 'auto',
                                transform: 'translateY(-50%)',
                                width: { xs: 180, sm: 300 },
                                height: { xs: 180, sm: 300 },
                                borderRadius: 2,
                                bgcolor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.28)' : 'rgba(255,255,255,0.64)',
                                opacity: 0.06,
                                zIndex: 0,
                                pointerEvents: 'none'
                              }} />
                              <Box sx={{
                                position: 'absolute',
                                top: '50%',
                                left: (dialogIndex % 2 === 0) ? 12 : 'auto',
                                right: (dialogIndex % 2 !== 0) ? 12 : 'auto',
                                transform: 'translateY(-50%)',
                                width: { xs: 200, sm: 340 },
                                height: { xs: 200, sm: 340 },
                                opacity: 0.22,
                                zIndex: 1,
                                pointerEvents: 'none',
                                filter: 'drop-shadow(0 10px 30px rgba(0,0,0,0.28)) saturate(1.03) contrast(1.05)'
                              }}
                                style={{ backgroundImage: `url(${decoImages[dialogIndex % decoImages.length]})`, backgroundRepeat: 'no-repeat', backgroundPosition: 'center', backgroundSize: 'contain' }}
                              />
                            </>
                          )}
                          {!readingMode ? (
                            <Typography variant="body1" sx={{ whiteSpace: 'pre-line', color: 'text.primary' }}>{highlights[dialogIndex]?.story}</Typography>
                          ) : (
                            <Typography variant="body1" sx={{ fontSize: 18, lineHeight: 1.8, color: 'text.primary' }}>{highlights[dialogIndex]?.story}</Typography>
                          )}
                        </Box>
                      </Box>
                      <KeyboardNavHandler activeIndex={dialogIndex} setIndex={setDialogIndex} onClose={closeDetail} total={highlights.length} />
                    </DialogContent>
                  <DialogActions sx={{ gap: 1, px: 3, py: 1 }}>
                    <Button onClick={() => setReadingMode(m => !m)} color="primary" variant="contained">{readingMode ? 'Exit Reading' : 'Reading Mode'}</Button>
                    <Box sx={{ flex: 1 }} />
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>{(dialogIndex ?? 0) + 1} / {highlights.length}</Typography>
                    <Button onClick={closeDetail} color="inherit">Close</Button>
                  </DialogActions>
                </Dialog>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </PublicLayout>
  );
}

export default HighlightsPage;

function KeyboardNavHandler({ activeIndex, setIndex, onClose, total }) {
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'ArrowLeft') setIndex(i => (i - 1 + total) % total);
      if (e.key === 'ArrowRight') setIndex(i => (i + 1) % total);
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [setIndex, onClose, total]);

  return null;
}
